import React, { useEffect, useState } from "react";
import { Compass, Leaf, Sprout, Plus, Pencil, Trash2, ExternalLink, Save, Link2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const ICONS = { compass: Compass, leaf: Leaf, sprout: Sprout };

// --- color helpers (derive hover + light tint from a single primary) ---
const clamp = (n) => Math.max(0, Math.min(255, n));
function hexToRgb(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h || "000000", 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const toHex = (r, g, b) => "#" + [r, g, b].map((x) => clamp(Math.round(x)).toString(16).padStart(2, "0")).join("");
const darken = (hex, amt = 0.15) => { const [r, g, b] = hexToRgb(hex); return toHex(r * (1 - amt), g * (1 - amt), b * (1 - amt)); };
const tint = (hex, amt = 0.9) => { const [r, g, b] = hexToRgb(hex); return toHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt); };

const EMPTY = { slug: "", name: "", tagline: "", logo_icon: "compass", logo_url: "", webhook_url: "", primary: "#2E5C41" };

export default function PartnersAdmin() {
  const [tenants, setTenants] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // slug being edited, or null for create
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [ghl, setGhl] = useState({ ghl_diagnostic_url: "", ghl_purchase_url: "" });
  const [ghlSaving, setGhlSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/admin/tenants");
      setTenants(data.tenants);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    }
  };
  useEffect(() => {
    load();
    client.get("/admin/settings").then(({ data }) => setGhl({
      ghl_diagnostic_url: data.ghl_diagnostic_url || "",
      ghl_purchase_url: data.ghl_purchase_url || "",
    })).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t.slug); setForm({ slug: t.slug, name: t.name, tagline: t.tagline || "", logo_icon: t.logo_icon || "compass", logo_url: t.logo_url || "", webhook_url: t.webhook_url || "", primary: t.primary || "#2E5C41" }); setOpen(true); };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Please choose an image under 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 256;
        let { width, height } = img;
        if (width >= height && width > MAX) { height = Math.round((height * MAX) / width); width = MAX; }
        else if (height > MAX) { width = Math.round((width * MAX) / height); height = MAX; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        let out = canvas.toDataURL("image/png");
        if (out.length > 120 * 1024) out = canvas.toDataURL("image/jpeg", 0.85);
        setForm((f) => ({ ...f, logo_url: out }));
      };
      img.onerror = () => toast.error("Could not read that image. Try a PNG or JPG.");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Partner name is required."); return; }
    const payload = {
      slug: form.slug, name: form.name, tagline: form.tagline, logo_icon: form.logo_icon,
      logo_url: form.logo_url || "", webhook_url: form.webhook_url || "",
      primary: form.primary, primary_hover: darken(form.primary, 0.15), primary_light: tint(form.primary, 0.9),
    };
    setSaving(true);
    try {
      if (editing) await client.put(`/admin/tenants/${editing}`, payload);
      else await client.post("/admin/tenants", payload);
      toast.success(editing ? "Partner updated." : "Partner created.");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete partner "${t.name}"? This can't be undone.`)) return;
    try {
      await client.delete(`/admin/tenants/${t.slug}`);
      toast.success("Partner deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const saveGhl = async () => {
    setGhlSaving(true);
    try {
      await client.put("/admin/settings", ghl);
      toast.success("CRM webhooks saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save webhook URLs."); }
    finally { setGhlSaving(false); }
  };

  if (forbidden) return <div className="mx-auto max-w-lg rounded-2xl border border-[#E5E2DC] bg-white p-10 text-center text-neutral-600">This area is for administrators only.</div>;
  if (!tenants) return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;

  const previewHover = darken(form.primary, 0.15);
  const previewLight = tint(form.primary, 0.9);
  const field = "min-h-[44px] w-full rounded-xl border border-neutral-200 px-3.5 outline-none transition-all focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--tenant-primary-light)]";

  return (
    <div className="fade-up space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Admin</p>
          <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">Partner organizations</h1>
          <p className="mt-2 text-neutral-600">Create and edit co-branded partners. Share their branded link as <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-sm">?cu=slug</code>.</p>
        </div>
        <button data-testid="add-partner-btn" onClick={openCreate} className="inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
          <Plus size={16} /> Add partner
        </button>
      </div>

      {/* CRM webhooks (Go High Level) — one URL per event */}
      <div className="rounded-2xl border border-[#E5E2DC] bg-white p-6" data-testid="ghl-settings-card">
        <div className="flex items-center gap-2 text-neutral-800"><Link2 size={18} className="tenant-text" /><h3 className="font-head text-lg font-medium">CRM webhooks (Go High Level)</h3></div>
        <p className="mt-1 text-sm text-neutral-500">Paste a GHL inbound webhook URL for each event. We POST a flat JSON payload when the event fires. Leave blank to disable an event.</p>
        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Diagnostic completed</label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input data-testid="ghl-diagnostic-input" value={ghl.ghl_diagnostic_url} onChange={(e) => setGhl({ ...ghl, ghl_diagnostic_url: e.target.value })} placeholder="https://services.leadconnectorhq.com/hooks/..." className={field + " sm:flex-1"} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Purchase completed</label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input data-testid="ghl-purchase-input" value={ghl.ghl_purchase_url} onChange={(e) => setGhl({ ...ghl, ghl_purchase_url: e.target.value })} placeholder="https://services.leadconnectorhq.com/hooks/..." className={field + " sm:flex-1"} />
            </div>
          </div>
          <button data-testid="ghl-save-btn" onClick={saveGhl} disabled={ghlSaving} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: "var(--tenant-primary)" }}>
            <Save size={15} /> {ghlSaving ? "Saving…" : "Save webhooks"}
          </button>
        </div>
      </div>

      {/* Partner list */}
      <div className="grid gap-4 sm:grid-cols-2">
        {tenants.map((t) => {
          const Icon = ICONS[t.logo_icon] || Compass;
          return (
            <div key={t.slug} className="rounded-2xl border border-[#E5E2DC] bg-white p-6" data-testid={`partner-card-${t.slug}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center overflow-hidden rounded-xl" style={{ backgroundColor: t.primary_light }}>
                    {t.logo_url
                      ? <img src={t.logo_url} alt={`${t.name} logo`} className="h-full w-full object-contain" data-testid={`partner-logo-img-${t.slug}`} />
                      : <Icon size={20} strokeWidth={1.75} style={{ color: t.primary }} />}
                  </span>
                  <div>
                    <p className="font-head text-lg font-medium text-neutral-900">{t.name}</p>
                    <p className="text-xs text-neutral-500">/?cu={t.slug} · {t.member_count} member{t.member_count === 1 ? "" : "s"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`edit-partner-${t.slug}`} onClick={() => openEdit(t)} className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100" title="Edit"><Pencil size={16} /></button>
                  {t.slug !== "fisbo" && (
                    <button data-testid={`delete-partner-${t.slug}`} onClick={() => remove(t)} className="rounded-lg p-2 text-neutral-400 hover:bg-red-50 hover:text-red-500" title="Delete"><Trash2 size={16} /></button>
                  )}
                </div>
              </div>
              {t.tagline && <p className="mt-3 text-sm text-neutral-600">{t.tagline}</p>}
              <div className="mt-4 flex items-center gap-2">
                {[t.primary, t.primary_hover, t.primary_light].map((c, i) => (
                  <span key={i} className="h-6 w-6 rounded-full border border-black/5" style={{ backgroundColor: c }} title={c} />
                ))}
                <a href={`/?cu=${t.slug}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs font-medium tenant-text hover:underline">Preview <ExternalLink size={12} /></a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="partner-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">{editing ? "Edit partner" : "Add partner"}</DialogTitle>
            <DialogDescription>Configure this co-branded partner's name, colors and logo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Partner name</label>
              <input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Navy Federal Credit Union" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Slug (URL id){editing && " — locked"}</label>
              <input data-testid="partner-slug-input" value={form.slug} disabled={!!editing} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className={field + (editing ? " bg-neutral-50 text-neutral-400" : "")} placeholder="navy-federal" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Tagline</label>
              <input data-testid="partner-tagline-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={field} placeholder="Grow your business, together." />
            </div>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Icon</label>
                <select data-testid="partner-icon-select" value={form.logo_icon} onChange={(e) => setForm({ ...form, logo_icon: e.target.value })} className={field}>
                  <option value="compass">Compass</option>
                  <option value="leaf">Leaf</option>
                  <option value="sprout">Sprout</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Brand color</label>
                <input data-testid="partner-color-input" type="color" value={form.primary} onChange={(e) => setForm({ ...form, primary: e.target.value })} className="h-[44px] w-16 cursor-pointer rounded-xl border border-neutral-200" />
              </div>
            </div>
            {/* logo upload */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Partner logo (optional)</label>
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50">
                  {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-full w-full object-contain" /> : <span className="text-[10px] text-neutral-400">icon</span>}
                </span>
                <input data-testid="partner-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoFile} className="text-sm text-neutral-600 file:mr-3 file:rounded-full file:border-0 file:bg-neutral-100 file:px-4 file:py-2 file:text-sm file:font-medium" />
                {form.logo_url && <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-neutral-500 underline">Remove</button>}
              </div>
              <p className="mt-1 text-xs text-neutral-400">Auto-resized for you. If no logo is uploaded, the chosen icon is used.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Partner CRM webhook (optional)</label>
              <input data-testid="partner-webhook-input" value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} className={field} placeholder="https://… (this partner's GHL hook)" />
              <p className="mt-1 text-xs text-neutral-400">Receives only this partner's member events.</p>
            </div>
            {/* live preview */}
            <div className="rounded-xl border border-[#E5E2DC] p-4" style={{ backgroundColor: previewLight }}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: previewHover }}>Preview</p>
              <div className="flex items-center gap-2">
                <span className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: form.primary }}>{form.name || "Partner"}</span>
                <span className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: previewHover }}>Hover</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100">Cancel</button>
            <button data-testid="partner-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: "var(--tenant-primary)" }}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create partner"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
